/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import defCashImg from 'assets/img/icons/def-cash-img.png';
import CounterInput from 'components/forms/CounterInput';
import { CanselCircleIcon } from 'assets/img/icons/icons';
import Button from 'components/base/Button';
import DropDownMoreSingleItems from './DropDownMoreSingleItems';
import { cashBackActions } from 'store/cash/cash.slice';

import './cashSale.scss';

interface CashBackListProps {
  cashBackListData?: any[];
  onAmountChange?: (itemId: string, amount: number) => void;
  selectedSalesValues?: any;
  setSelectedSalesValues?: any;
  setCashBackListData?: any;
  setSelectedOptionMoreItem?: any;
}

const CashBackList = ({
  cashBackListData,
  onAmountChange,
  selectedSalesValues,
  setSelectedSalesValues,
  setCashBackListData,
  setSelectedOptionMoreItem
}: CashBackListProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [amounts, setAmounts] = useState<{ [key: string]: number }>({});
  const hasWholesalePrice = selectedSalesValues.some(
    (item: any) => item.name === 'wholesale-price' && item.value === true
  );

  const handleAmountChange = (itemId: string, newAmount: number) => {
    setAmounts(prev => ({
      ...prev,
      [itemId]: newAmount
    }));

    if (setCashBackListData && cashBackListData) {
      const item = cashBackListData.find(i => i.id === itemId);
      if (item) {
        const price = hasWholesalePrice
          ? item.price_whosale || 0
          : item.price || 0;
        const total = price * newAmount;

        setCashBackListData((prev: any) =>
          prev.map((i: any) =>
            i.id === itemId
              ? {
                  ...i,
                  updated_amount: newAmount,
                  payment_amount: price,
                  payment_amount_total: total,
                  payment_amount_wholesale: hasWholesalePrice
                    ? total
                    : i.payment_amount_wholesale
                }
              : i
          )
        );
      }
    }

    // Call parent callback if provided
    onAmountChange?.(itemId, newAmount);
  };

  const totalPrice = useMemo(() => {
    return (
      cashBackListData?.reduce((sum, item) => {
        const amount = item.updated_amount || amounts[item.id] || 1;
        const price = hasWholesalePrice
          ? item.price_whosale || 0
          : item.price || 0;
        return sum + price * amount;
      }, 0) || 0
    );
  }, [cashBackListData, amounts, hasWholesalePrice]);

  const handlePayment = () => {
    // Dispatch action to save cashBackListData and selectedSalesValues to Redux
    if (cashBackListData && selectedSalesValues) {
      dispatch(
        cashBackActions.saveCashBackList({
          cashBack: cashBackListData,
          selectedSalesValues,
          totalPrice
        })
      );
      navigate('/cash-sale/payment');
    }
  };

  const handleRemoveDiscount = (type: string) => {
    if (setSelectedSalesValues) {
      setSelectedSalesValues((prev: any) =>
        prev.filter((item: any) => item.name !== type)
      );
      setSelectedOptionMoreItem(null);
    }
  };
  const discountAmount = useMemo(() => {
    const amountItem = selectedSalesValues?.find(
      (item: any) => item.name === 'discount-amount' && item.value !== null
    );
    return amountItem?.value || 0;
  }, [selectedSalesValues]);

  const discountPercent = useMemo(() => {
    const percentItem = selectedSalesValues?.find(
      (item: any) => item.name === 'discount-percent' && item.value !== null
    );
    return percentItem?.value || 0;
  }, [selectedSalesValues]);

  const discountValue = useMemo(() => {
    if (discountAmount > 0) {
      return discountAmount;
    } else if (discountPercent > 0) {
      return (totalPrice * discountPercent) / 100;
    }
    return 0; // No discount
  }, [totalPrice, discountAmount, discountPercent]);

  const discountedTotal = useMemo(() => {
    return Math.max(0, totalPrice - discountValue);
  }, [totalPrice, discountValue]);

  const hasDiscount =
    selectedSalesValues.some(
      (item: any) => item.name === 'discount-amount' && item.value > 0
    ) ||
    selectedSalesValues.some(
      (item: any) => item.name === 'discount-percent' && item.value > 0
    );

  return (
    <div className="cashback-list">
      <div className="cashback-items">
        {selectedSalesValues?.length > 0 && (
          <div className="cashback-filters">
            {selectedSalesValues.some(
              (item: any) => item.name === 'discount-percent' && item.value > 0
            ) && (
              <div className="filter-box">
                <span>
                  {t('Процентная скидка')}:{' '}
                  {
                    selectedSalesValues.find(
                      (item: any) => item.name === 'discount-percent'
                    )?.value
                  }
                  %
                </span>
                <button
                  className="close-btn"
                  onClick={() => handleRemoveDiscount('discount-percent')}
                  aria-label="Remove discount percent"
                >
                  <CanselCircleIcon />
                </button>
              </div>
            )}
            {selectedSalesValues.some(
              (item: any) => item.name === 'discount-amount' && item.value > 0
            ) && (
              <div className="filter-box">
                <span>
                  {t('Сумма скидки')}:{' '}
                  {
                    selectedSalesValues.find(
                      (item: any) => item.name === 'discount-amount'
                    )?.value
                  }
                </span>
                <button
                  className="close-btn"
                  onClick={() => handleRemoveDiscount('discount-amount')}
                  aria-label="Remove discount amount"
                >
                  <CanselCircleIcon />
                </button>
              </div>
            )}
            {selectedSalesValues.some(
              (item: any) =>
                item.name === 'wholesale-price' && item.value === true
            ) && (
              <div className="filter-box">
                <span>{t('Применить оптовую цену')}</span>
                <button
                  className="close-btn"
                  onClick={() => handleRemoveDiscount('wholesale-price')}
                  aria-label="Remove wholesale price"
                >
                  <CanselCircleIcon />
                </button>
              </div>
            )}
          </div>
        )}

        {cashBackListData?.map((item: any) => {
          const price = hasWholesalePrice
            ? item.price_whosale || 0
            : item.price || 0;
          const amount = item.updated_amount || amounts[item.id] || 1;
          const itemTotal = price * amount;
          console.log('price', price);

          const itemDiscount =
            discountPercent > 0
              ? (itemTotal * (discountPercent / cashBackListData?.length)) / 100
              : 0;
          const discountedPricePerItem = itemTotal - itemDiscount;

          return (
            <div key={item?.id} className="cashback-item">
              <div className="file-thumbnail">
                <img
                  className="fit-cover"
                  src={item?.photo ? item?.photo : defCashImg}
                />
              </div>

              <p className="cashback-name">{item?.name || ''}</p>

              <div className="cashback-amount">
                <CounterInput
                  value={item?.updated_amount || amounts[item?.id] || 1}
                  onChange={newAmount =>
                    handleAmountChange(item?.id, newAmount)
                  }
                  min={1}
                  max={999}
                  placeholder="0"
                />

                {
                  /* prettier-ignore  */
                  (item.updated_amount || amounts[item.id] || 1) >= 2 && (
                  <p className="amount-info">
                    {price
                      ? new Intl.NumberFormat('en-US', {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 4,
                          useGrouping: true
                        })
                          .format(price)
                          .replace(/,/g, ' ')
                      : 0}{' '}
                    uzs/шт
                  </p>
                )
                }
              </div>

              <div className="cashback-prices">
                {
                  /* prettier-ignore  */
                  discountPercent > 0 && (
                  <p className="cashback-total-price">
                    {discountedPricePerItem
                      ? new Intl.NumberFormat('en-US', {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 4,
                          useGrouping: true
                        })
                          .format(discountedPricePerItem)
                          .replace(/,/g, ' ')
                      : 0}
                  </p>
                )
                }
                <p
                  /* prettier-ignore */
                  className={`cashback-total-price ${discountPercent > 0 ? 'discounted' : ''}`}
                >
                  {itemTotal
                    ? new Intl.NumberFormat('en-US', {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 4,
                        useGrouping: true
                      })
                        .format(itemTotal)
                        .replace(/,/g, ' ')
                    : 0}
                </p>
              </div>

              <DropDownMoreSingleItems
                cashListData={item}
                setCashBackListData={setCashBackListData}
              />
            </div>
          );
        })}
      </div>

      <div className="cashback-total">
        <div className="total-items-info">
          <p className="total-title">
            {t('products')} ({cashBackListData?.length || 0})
          </p>
          <p className="total-price">
            {new Intl.NumberFormat('en-US', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 4,
              useGrouping: true
            })
              .format(totalPrice)
              .replace(/,/g, ' ')}
          </p>
        </div>

        {hasDiscount && (
          <div className="total-discount-info">
            <p className="total-discount-title">{t('discount')}</p>
            <p className="total-discount-price">
              {new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 4,
                useGrouping: true
              })
                .format(discountValue)
                .replace(/,/g, ' ')}
            </p>
          </div>
        )}

        <div className="total-info">
          <p className="info-title">{t('result')}</p>
          <p className="info-price">
            {new Intl.NumberFormat('en-US', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 4,
              useGrouping: true
            })
              .format(discountedTotal)
              .replace(/,/g, ' ')}
          </p>
        </div>

        <Button
          onClick={handlePayment}
          style={{ height: '48px', width: '100%' }}
          variant="primary"
        >
          {t('payment')}
        </Button>
      </div>
    </div>
  );
};

export default CashBackList;
